import React from "react";
import { CheckCircleOutlined } from "@ant-design/icons";
import CustomCard from "../../../components/CustomCard";
import { FilePreviewModal } from "../../../components/FilePreviewModal";
import styles from "./SignInfoView.module.css";

interface SignInfoViewProps {
  signInfo: any;
  signPhotoDisplayUrl: string;
  signatureDisplayUrls: Record<number, string>;
  signPhotoPreviewOpen: boolean;
  signPhotoPreviewFile?: { url: string; name: string };
  onSignPhotoPreviewOpenChange: (open: boolean) => void;
  onSignPhotoPreviewFileChange: (file: { url: string; name: string }) => void;
  signatureImagePreviewOpen: boolean;
  signatureImagePreviewFile?: { url: string; name: string };
  onSignatureImagePreviewOpenChange: (open: boolean) => void;
  onSignatureImagePreviewFileChange: (file: { url: string; name: string }) => void;
}

export function SignInfoView({
  signInfo,
  signPhotoDisplayUrl,
  signatureDisplayUrls,
  signPhotoPreviewOpen,
  signPhotoPreviewFile,
  onSignPhotoPreviewOpenChange,
  onSignPhotoPreviewFileChange,
  signatureImagePreviewOpen,
  signatureImagePreviewFile,
  onSignatureImagePreviewOpenChange,
  onSignatureImagePreviewFileChange,
}: SignInfoViewProps) {
  // 获取签名类型名称映射
  const signatureTypeNames: Record<string, string> = {
    POLICYHOLDER: "投保人簽名",
    INSURED: "受保人簽名",
    ADVISOR: "顧問簽名",
    WITNESS: "見證人簽名",
  };

  return (
    <div className={styles["detail-content"]}>
      {/* OCR识别结果和照片展示 */}
      <CustomCard title="簽署資料" bordered={false} className={styles["detail-card"]}>
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          {/* Step 1: 照片展示 */}
          <div className="step-row" style={{ display: "flex" }}>
            <div style={{ marginRight: 16, display: "flex", flexDirection: "column", alignItems: "center" }}>
              {signInfo?.photo ? (
                <CheckCircleOutlined style={{ fontSize: 24, color: "var(--theme-primary)" }} />
              ) : (
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    border: "2px solid #d9d9d9",
                    color: "#d9d9d9",
                    textAlign: "center",
                    lineHeight: "20px",
                    fontWeight: "bold",
                  }}
                >
                  1
                </div>
              )}
              <div style={{ width: 1, background: "#e8e8e8", flex: 1, marginTop: 8, minHeight: 40 }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>01 客戶合照</div>
              {signPhotoDisplayUrl ? (
                <div style={{ position: "relative", display: "inline-block" }}>
                  <img
                    src={signPhotoDisplayUrl}
                    alt="客戶合照"
                    style={{
                      width: 160,
                      height: 160,
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid #d9d9d9",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      onSignPhotoPreviewFileChange({ url: signPhotoDisplayUrl, name: "客戶合照" });
                      onSignPhotoPreviewOpenChange(true);
                    }}
                  />
                </div>
              ) : (
                <div style={{ color: "#999", fontSize: 14 }}>未上載照片</div>
              )}
            </div>
          </div>

          {/* Step 2: 签名展示 */}
          <div className="step-row" style={{ display: "flex" }}>
            <div style={{ marginRight: 16, display: "flex", flexDirection: "column", alignItems: "center" }}>
              {signInfo?.signatures && signInfo.signatures.length > 0 ? (
                <CheckCircleOutlined style={{ fontSize: 24, color: "var(--theme-primary)" }} />
              ) : (
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    border: "2px solid #d9d9d9",
                    color: "#d9d9d9",
                    textAlign: "center",
                    lineHeight: "20px",
                    fontWeight: "bold",
                  }}
                >
                  2
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>02 簽名</div>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                {signInfo?.signatures?.map((sig: any, index: number) => {
                  const displayName = signatureTypeNames[sig.type] || sig.type;
                  const displayUrl = signatureDisplayUrls[index];

                  if (!displayUrl) {
                    return null;
                  }

                  return (
                    <div key={index} style={{ textAlign: "center" }}>
                      <div
                        style={{
                          width: 140,
                          height: 140,
                          border: "2px solid var(--theme-primary)",
                          borderRadius: 4,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#fff",
                          overflow: "hidden",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          onSignatureImagePreviewFileChange({ url: displayUrl, name: displayName });
                          onSignatureImagePreviewOpenChange(true);
                        }}
                      >
                        <img
                          src={displayUrl}
                          alt={displayName}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                      <div style={{ marginTop: 8, fontSize: 14, color: "#666" }}>{displayName}</div>
                    </div>
                  );
                })}
                {(!signInfo?.signatures || signInfo.signatures.length === 0) && <div style={{ color: "#999", fontSize: 14 }}>暫無簽名</div>}
              </div>
            </div>
          </div>
        </div>
      </CustomCard>

      {/* 照片预览 Modal */}
      <FilePreviewModal open={signPhotoPreviewOpen} onClose={() => onSignPhotoPreviewOpenChange(false)} fileUrl={signPhotoPreviewFile?.url} fileName={signPhotoPreviewFile?.name} fileType="image" />

      {/* 签名预览 Modal */}
      <FilePreviewModal
        open={signatureImagePreviewOpen}
        onClose={() => onSignatureImagePreviewOpenChange(false)}
        fileUrl={signatureImagePreviewFile?.url}
        fileName={signatureImagePreviewFile?.name}
        fileType="image"
      />
    </div>
  );
}
